import { Box, Container, Typography, alpha } from '@mui/material';
import { IconButton } from '@mui/material';
import { ArrowBack, Gavel } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout';
import { brandColors } from '@/theme/colors';

export const TermsPage = () => {
  const navigate = useNavigate();
  const effectiveDate = 'January 1, 2025';

  return (
    <AppLayout title="Terms of Service">
      {/* Hero Header */}
      <Box
        sx={{
          background: brandColors.gradient,
          py: { xs: 4, md: 6 },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 40%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Gavel sx={{ fontSize: 40, color: 'white' }} />
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'white',
                textShadow: '0 2px 10px rgba(0,0,0,0.2)',
              }}
            >
              Terms of Service
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              ml: 7,
              fontWeight: 400,
            }}
          >
            Effective Date: {effectiveDate}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pb: 8 }}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 3,
            p: { xs: 3, md: 5 },
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            '& h2': {
              fontSize: '1.5rem',
              fontWeight: 600,
              mt: 4,
              mb: 2,
              color: brandColors.charcoal,
              borderBottom: `2px solid ${alpha(brandColors.purple, 0.2)}`,
              pb: 1,
            },
            '& h3': {
              fontSize: '1.2rem',
              fontWeight: 600,
              mt: 3,
              mb: 1.5,
              color: brandColors.purpleDeep,
            },
            '& p': {
              mb: 2,
              lineHeight: 1.8,
              color: '#555',
            },
            '& ul': {
              mb: 2,
              pl: 3,
            },
            '& li': {
              mb: 1,
              lineHeight: 1.7,
              color: '#555',
            },
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', color: '#666' }}>
            Please read these Terms of Service carefully before using ThinkSpace.
          </Typography>

          <Typography variant="h2">1. Agreement to Terms</Typography>
          <Typography variant="body1">
            By accessing or using ThinkSpace (the "Service"), provided by Advancer Limited ("Company", "we", "us", or "our"),
            a New Zealand registered company, you agree to be bound by these Terms of Service ("Terms"). If you do not agree
            to these Terms, do not use the Service.
          </Typography>

          <Typography variant="h2">2. Description of Service</Typography>
          <Typography variant="body1">
            ThinkSpace is a collaborative visual thinking and whiteboard application that allows users to create,
            edit, and share digital canvases containing sticky notes, shapes, text, images, and other visual elements.
          </Typography>

          <Typography variant="h2">3. User Accounts</Typography>
          <Typography variant="body1">
            To use the Service, you must create an account using Google authentication. You are responsible for:
          </Typography>
          <ul>
            <li>Maintaining the security of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Ensuring your use complies with these Terms</li>
          </ul>

          <Typography variant="h2">4. Acceptable Use</Typography>
          <Typography variant="body1">
            You agree not to use the Service to:
          </Typography>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon intellectual property rights of others</li>
            <li>Upload malicious software or harmful content</li>
            <li>Attempt to gain unauthorized access to the Service or other accounts</li>
            <li>Interfere with or disrupt the Service's operation</li>
            <li>Share content that is illegal, defamatory, or harmful</li>
          </ul>

          <Typography variant="h2">5. Intellectual Property</Typography>
          <Typography variant="h3">5.1 Your Content</Typography>
          <Typography variant="body1">
            You retain ownership of content you create using the Service. By using the Service, you grant us a
            limited license to store, process, and display your content solely for the purpose of providing the Service.
          </Typography>
          <Typography variant="h3">5.2 Our Property</Typography>
          <Typography variant="body1">
            The Service, including its design, features, and underlying technology, is owned by Advancer Limited
            and protected by intellectual property laws.
          </Typography>

          <Typography variant="h2">6. Payment and Subscription</Typography>
          <Typography variant="body1">
            The Service offers a free trial period followed by a paid subscription. Pricing is displayed on our
            website and is subject to change with reasonable notice. Subscriptions are billed in advance and are
            non-refundable except as required by law.
          </Typography>

          <Typography variant="h2">7. Limitation of Liability</Typography>
          <Typography variant="body1" sx={{ fontWeight: 500, color: `${brandColors.charcoal} !important` }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, ADVANCER LIMITED'S TOTAL LIABILITY TO YOU FOR ANY CLAIMS
            ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED THE LESSER OF:
          </Typography>
          <ul>
            <li><strong>Five Hundred United States Dollars (USD $500)</strong>; or</li>
            <li><strong>The total fees paid by you to Advancer Limited in the twelve (12) months preceding the claim</strong></li>
          </ul>
          <Typography variant="body1">
            This limitation applies to all claims, whether based in contract, tort, statute, or any other legal theory,
            and whether or not Advancer Limited has been advised of the possibility of such damages.
          </Typography>

          <Typography variant="h2">8. Disclaimer of Warranties</Typography>
          <Typography variant="body1">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </Typography>

          <Typography variant="h2">9. Indemnification</Typography>
          <Typography variant="body1">
            You agree to indemnify and hold harmless Advancer Limited, its officers, directors, employees, and agents
            from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.
          </Typography>

          <Typography variant="h2">10. Third-Party Services</Typography>
          <Typography variant="body1">
            The Service utilizes Google Firebase for authentication, data storage, and hosting. Your use of the
            Service is also subject to Google's Terms of Service and Privacy Policy. We are not responsible for
            the practices or content of third-party services.
          </Typography>

          <Typography variant="h2">11. Termination</Typography>
          <Typography variant="body1">
            We may suspend or terminate your access to the Service at any time, with or without cause, and with
            or without notice. Upon termination, your right to use the Service ceases immediately. You may delete
            your account at any time through the Service settings.
          </Typography>

          <Typography variant="h2">12. Dispute Resolution</Typography>
          <Typography variant="h3">12.1 Arbitration</Typography>
          <Typography variant="body1">
            Any dispute arising from these Terms or the Service shall be resolved through binding arbitration
            administered in accordance with the Arbitration Act 1996 (New Zealand). The arbitration shall take
            place in Auckland, New Zealand, and shall be conducted in English. The decision of the arbitrator
            shall be final and binding.
          </Typography>
          <Typography variant="h3">12.2 Exceptions</Typography>
          <Typography variant="body1">
            Either party may seek injunctive relief in the courts of New Zealand for intellectual property
            violations or unauthorized access to the Service.
          </Typography>

          <Typography variant="h2">13. Governing Law</Typography>
          <Typography variant="body1">
            These Terms shall be governed by and construed in accordance with the laws of New Zealand,
            without regard to its conflict of law provisions. Subject to the arbitration clause above,
            the courts of New Zealand shall have exclusive jurisdiction over any disputes.
          </Typography>

          <Typography variant="h2">14. Changes to Terms</Typography>
          <Typography variant="body1">
            We may modify these Terms at any time by posting the updated Terms on the Service. Your continued
            use of the Service after changes are posted constitutes acceptance of the modified Terms. We will
            provide reasonable notice of material changes.
          </Typography>

          <Typography variant="h2">15. Severability</Typography>
          <Typography variant="body1">
            If any provision of these Terms is found to be unenforceable, the remaining provisions shall
            continue in full force and effect.
          </Typography>

          <Typography variant="h2">16. Force Majeure</Typography>
          <Typography variant="body1">
            Advancer Limited shall not be liable for any failure or delay in performing its obligations where such
            failure or delay results from circumstances beyond its reasonable control, including but not limited to
            natural disasters, acts of government, internet outages, or third-party service provider failures.
          </Typography>

          <Typography variant="h2">17. Entire Agreement</Typography>
          <Typography variant="body1">
            These Terms, together with our Privacy Policy, constitute the entire agreement between you and
            Advancer Limited regarding the Service and supersede all prior agreements and understandings.
          </Typography>

          <Typography variant="h2">18. Waiver</Typography>
          <Typography variant="body1">
            The failure of Advancer Limited to enforce any right or provision of these Terms shall not constitute
            a waiver of such right or provision. Any waiver must be in writing and signed by an authorized
            representative of Advancer Limited.
          </Typography>

          <Typography variant="h2">19. Contact Information</Typography>
          <Typography variant="body1">
            For questions about these Terms, please contact us at:
          </Typography>
          <Box sx={{
            bgcolor: alpha(brandColors.purple, 0.05),
            p: 3,
            borderRadius: 2,
            borderLeft: `4px solid ${brandColors.purple}`,
            mt: 2,
          }}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Advancer Limited</strong> (NZBN: 9429041814352)
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              2/14 Parkway Drive, Mairangi Bay
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              Auckland 0632, New Zealand
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              Email: info@mythinkspace.app
            </Typography>
            <Typography variant="body1">
              Phone: +64 9 489 3830
            </Typography>
          </Box>
        </Box>
      </Container>
    </AppLayout>
  );
};
